// FILE: src/pages/Analytics.jsx
import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import Card from '../components/ui/Card';
import ChartCard from '../components/ui/ChartCard';

const Analytics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('7d');

  // Key metrics
  const keyMetrics = [
    { 
      title: 'Total Revenue', 
      value: '$124,567', 
      change: '+18.2%', 
      trend: 'up', 
      icon: '💰',
      description: 'vs last period'
    },
    { 
      title: 'Active Users', 
      value: '8,439', 
      change: '+12.5%', 
      trend: 'up', 
      icon: '👥',
      description: 'monthly active users'
    },
    { 
      title: 'Conversion Rate', 
      value: '3.24%', 
      change: '-0.8%', 
      trend: 'down', 
      icon: '📊',
      description: 'visitor to customer'
    },
    { 
      title: 'Avg Order Value', 
      value: '$67.89', 
      change: '+5.2%', 
      trend: 'up', 
      icon: '🛒',
      description: 'per transaction'
    }
  ];

  // Revenue analytics data
  const revenueData = [
    { name: 'Jan', revenue: 45000, profit: 18000, expenses: 27000 },
    { name: 'Feb', revenue: 52000, profit: 21500, expenses: 30500 },
    { name: 'Mar', revenue: 48000, profit: 19200, expenses: 28800 },
    { name: 'Apr', revenue: 61000, profit: 25400, expenses: 35600 },
    { name: 'May', revenue: 55000, profit: 22000, expenses: 33000 },
    { name: 'Jun', revenue: 67000, profit: 28150, expenses: 38850 },
    { name: 'Jul', revenue: 72000, profit: 30600, expenses: 41400 },
    { name: 'Aug', revenue: 68000, profit: 28900, expenses: 39100 },
    { name: 'Sep', revenue: 74000, profit: 31450, expenses: 42550 },
    { name: 'Oct', revenue: 81000, profit: 34650, expenses: 46350 },
    { name: 'Nov', revenue: 76000, profit: 32300, expenses: 43700 },
    { name: 'Dec', revenue: 89000, profit: 38250, expenses: 50750 }
  ];

  // User behavior data
  const userBehaviorData = [
    { name: 'Landing Page', visitors: 15678, bounceRate: 42 },
    { name: 'Product Pages', visitors: 12450, bounceRate: 28 },
    { name: 'Category Pages', visitors: 8934, bounceRate: 35 },
    { name: 'Cart Page', visitors: 5623, bounceRate: 65 },
    { name: 'Checkout', visitors: 3421, bounceRate: 18 },
    { name: 'Thank You', visitors: 2156, bounceRate: 5 }
  ];

  // Traffic sources
  const trafficSources = [
    { name: 'Organic Search', value: 45.2, visitors: 18456 },
    { name: 'Direct', value: 28.7, visitors: 11734 },
    { name: 'Social Media', value: 12.3, visitors: 5023 },
    { name: 'Email Marketing', value: 8.1, visitors: 3312 },
    { name: 'Paid Ads', value: 5.7, visitors: 2330 }
  ];

  // Device analytics
  const deviceData = [
    { name: 'Desktop', value: 52.4, sessions: 21456 },
    { name: 'Mobile', value: 38.9, sessions: 15923 },
    { name: 'Tablet', value: 8.7, sessions: 3567 }
  ];

  // Geographic data
  const geographicData = [
    { name: 'United States', value: 8934, percentage: 34.2 },
    { name: 'United Kingdom', value: 5623, percentage: 21.5 },
    { name: 'Germany', value: 3421, percentage: 13.1 },
    { name: 'France', value: 2890, percentage: 11.1 },
    { name: 'Canada', value: 2156, percentage: 8.3 },
    { name: 'Others', value: 3089, percentage: 11.8 }
  ];

  // Top products performance
  const topProducts = [
    { name: 'Wireless Headphones', revenue: 45678, units: 234, growth: 18.5 },
    { name: 'Smart Watch', revenue: 38912, units: 198, growth: 12.3 },
    { name: 'Laptop Stand', revenue: 29456, units: 456, growth: -3.2 },
    { name: 'USB-C Hub', revenue: 24590, units: 389, growth: 25.8 },
    { name: 'Wireless Mouse', revenue: 19823, units: 567, growth: 8.9 }
  ];

  return (
    <div>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analytics</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Comprehensive business intelligence and insights
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
              Export Report
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {keyMetrics.map((metric, index) => (
            <Card key={index} className="relative overflow-hidden">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{metric.title}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{metric.value}</p>
                  <div className="flex items-center mt-1">
                    <span className={`text-sm font-medium ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                      {metric.change}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">{metric.description}</span>
                  </div>
                </div>
                <div className="text-2xl">{metric.icon}</div>
              </div>
            </Card>
          ))}
        </div>

        {/* Revenue Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard 
            title="Revenue vs Profit Analysis" 
            subtitle="Monthly financial performance"
            chartType="area"
            data={revenueData}
            dataKey={['revenue', 'profit']}
            xAxisKey="name"
            colors={['#3B82F6', '#10B981']}
            className="lg:col-span-2"
          />
        </div>

        {/* Traffic and User Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Traffic Sources */}
          <ChartCard 
            title="Traffic Sources" 
            subtitle="Where your visitors come from"
            chartType="pie"
            data={trafficSources}
            dataKey="value"
            colors={['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444']}
          />

          {/* Device Analytics */}
          <ChartCard 
            title="Device Usage" 
            subtitle="Visitor device preferences"
            chartType="donut"
            data={deviceData}
            dataKey="value"
            colors={['#3B82F6', '#10B981', '#8B5CF6']}
          />
        </div>

        {/* User Behavior and Geographic Data */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Behavior Funnel */}
          <ChartCard 
            title="User Journey Funnel" 
            subtitle="Page visit patterns and bounce rates"
            chartType="bar"
            data={userBehaviorData}
            dataKey="visitors"
            xAxisKey="name"
            colors={['#8B5CF6']}
          />

          {/* Geographic Distribution */}
          <Card title="Geographic Distribution" subtitle="Visitors by country">
            <div className="space-y-4">
              {geographicData.map((country, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-blue-500 mr-3"></div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{country.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-900 dark:text-white">{country.value.toLocaleString()}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{country.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Top Products Performance */}
        <Card title="Top Products Performance" subtitle="Best performing products by revenue">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Units Sold
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Growth
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {topProducts.map((product, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{product.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">${product.revenue.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{product.units}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        product.growth >= 0 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                      }`}>
                        {product.growth >= 0 ? '+' : ''}{product.growth}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;